/**
 * URL guard for briefing-supplied destinations.
 *
 * The Pulse renderer surfaces links sourced from external connectors
 * (GitHub PRs, HubSpot deals, Gmail threads, etc.). React strips
 * `javascript:` from inline `href` attributes, but `target="_blank"` and
 * `window.open` are not protected — a malformed connector payload could
 * land an executable URL on the page. Centralise the safety check so
 * every anchor/popup site funnels through the same allowlist.
 *
 * Returns the URL unchanged if it parses to an http(s) or mailto target,
 * otherwise returns null. Callers should treat null as "no safe URL" and
 * either fall back to plain text or hide the link entirely.
 */

const SAFE_SCHEMES = new Set(["http:", "https:", "mailto:"]);

export function safeExternalUrl(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  // Relative paths are safe (they cannot escape the origin) but the
  // legacy renderer has no need for them — every link in this surface
  // points to an external connector, so reject anything that does not
  // parse as an absolute URL.
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  if (!SAFE_SCHEMES.has(parsed.protocol)) return null;
  return trimmed;
}
